import * as React from 'react';
import type { DomainEntity } from '@web-config-app/core';
import { isDomainEntity } from '@web-config-app/core';
import { useEntityReference } from '../../hooks/use-entity-reference/use-entity-reference';
import { useEntitySchema } from '../../hooks/use-entity-schema/use-entity-schema';

export interface EntityLoaderProps {
  entityType: string;
  render: (domainEntity: DomainEntity) => React.ReactNode;
}

/**
 * EntityLoader component is responsible for loading the entity schema asynchronously
 * via the `useEntitySchema` hook since the schema is stored separately from the entity configuration.
 *
 * @param {EntityLoaderProps} props - The properties for the EntityLoader component.
 * @param {string} props.entityType - The type of the entity to be loaded.
 * @param {(domainEntity: DomainEntity) => React.ReactNode} props.render - A render function that takes an entity and returns a React node.
 *
 * @returns {React.ReactNode | null} - Returns the rendered entity if it is valid, otherwise returns null.
 */

export const EntityLoader = ({
  entityType,
  render,
}: React.PropsWithChildren<EntityLoaderProps>) => {
  const entityConfig = useEntityReference({ entityType });
  const { schema: entitySchema, isError } = useEntitySchema({
    entityId: entityConfig.id,
  });

  if (isError) {
    throw new Error(`Failed to load schema for entity type "${entityType}".`);
  }

  const entity = {
    ...entityConfig,
    schema: entitySchema,
  };

  /**
   * Since the schema is loaded asynchronously, we need to wait to render contents until the schema has been loaded.
   */
  return isDomainEntity(entity) ? <>{render(entity)}</> : null;
};
